import {
  DeleteOutlined as DeleteOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';
import { Box, IconButton, Table, TableBody, TableRow } from '@mui/material';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { SimpleDialog } from 'src/components/modal';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { Snackbar } from 'src/components/snackbar';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import { useModal } from 'src/hooks';
import { IComponentTag, IComponentTagWithComponentCount } from 'src/interfaces';
import { DataTestID, lang, useUserRole } from 'src/utils';

import { useManageComponentTagPage } from '../../hooks/ManageComponentTagPage.hook';
import {
  ArrowIcon,
  CustomTableCell,
  CustomTableCellRight,
  CustomTableHead,
  CustomTableRow,
  InViewBoxContainer,
  KeyboardArrowDownIconStyled,
  KeyboardArrowUpIconStyled,
  TableBodyContainer,
} from '../../ManageComponentTag.styled';
import { SORT_DIRECTION } from '../../ManageComponentTagPage.interface';

export const ManageComponentTagGridTable = () => {
  const navigate = useNavigate();
  const { ref: inViewRef, inView } = useInView({ threshold: 0.1 });
  const [selectedComponentTag, setSelectedComponentTag] = useState(0);
  const [deletingStatus, setDeletingStatus] = useState<{
    [key: number]: boolean;
  }>({});
  const deleteConfirmDialog = useModal();
  const { isViewOnly } = useUserRole();
  const {
    isLoading,
    getNextComponentTagWithComponentCountPage,
    deleteComponentTag,
    columns,
    handleSort,
    isActive,
    activeColumn,
    componentTagWithComponentCount,
    sorting,
    hasNextPage,
  } = useManageComponentTagPage();

  useEffect(() => {
    if (inView) {
      getNextComponentTagWithComponentCountPage();
    }
  }, [getNextComponentTagWithComponentCountPage, inView]);

  if (isLoading) {
    return <Progress />;
  }
  const hasData =
    componentTagWithComponentCount &&
    componentTagWithComponentCount?.pages.flatMap((page) => page.data).length >
      0;

  const componentTagWithComponentCountData = (
    row: IComponentTagWithComponentCount,
    column: (typeof columns)[number],
  ) => {
    const columnValues =
      row[column.key as keyof IComponentTagWithComponentCount];

    if (typeof columnValues === 'string' && columnValues.length > 30) {
      return (
        <CustomTooltip
          width={500}
          placement="bottom"
          title={columnValues}
          overflowWidth={250}>
          <span>{columnValues}</span>
        </CustomTooltip>
      );
    }

    return <span>{columnValues}</span>;
  };

  const deleteTag = async (tag: IComponentTag) => {
    try {
      setDeletingStatus((prevStatus) => ({
        ...prevStatus,
        [tag.id]: true,
      }));
      await deleteComponentTag.mutateAsync(tag.id);
      Snackbar.success(`${tag?.name} ${lang.successfullyDeleted}`);
      deleteConfirmDialog.onCloseModal();
    } catch (error: any) {
      Snackbar.error(lang.failedToDeleteTag);
    }
  };

  return (
    <>
      {hasData ? (
        <TableBodyContainer>
          <Table stickyHeader>
            <CustomTableHead>
              <TableRow>
                {columns.map((column) => (
                  <CustomTableCell key={column.key} isdoublewidth={column.key}>
                    <Box
                      display="flex"
                      border="none"
                      alignItems="center"
                      justifyContent="start"
                      gap={3}>
                      {column.label}
                      {column.sortIcon && (
                        <Box
                          width={14}
                          height={31}
                          display="flex"
                          flexDirection="column"
                          justifyContent="space-around"
                          marginRight={0}>
                          <ArrowIcon
                            onClick={() =>
                              handleSort(column.key, SORT_DIRECTION.ASC)
                            }>
                            <KeyboardArrowUpIconStyled
                              data-testid={
                                DataTestID.ManageComponentTagUpArrowIcon
                              }
                              active={
                                activeColumn === column.key &&
                                sorting.order === SORT_DIRECTION.ASC &&
                                isActive
                              }
                            />
                          </ArrowIcon>
                          <ArrowIcon
                            onClick={() =>
                              handleSort(column.key, SORT_DIRECTION.DESC)
                            }>
                            <KeyboardArrowDownIconStyled
                              data-testid={
                                DataTestID.ManageComponentTagDownArrowIcon
                              }
                              active={
                                activeColumn === column.key &&
                                sorting.order === SORT_DIRECTION.DESC &&
                                isActive
                              }
                            />
                          </ArrowIcon>
                        </Box>
                      )}
                    </Box>
                  </CustomTableCell>
                ))}
                <CustomTableCell />
                <CustomTableCell />
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {componentTagWithComponentCount?.pages.map((page) =>
                page.data.map((row) => (
                  <CustomTableRow key={row.id}>
                    {columns.map((column) => (
                      <CustomTableCell
                        key={column.key}
                        isdoublewidth={column.key}>
                        {componentTagWithComponentCountData(row, column)}
                      </CustomTableCell>
                    ))}
                    <CustomTableCellRight>
                      {!isViewOnly && (
                        <IconButton
                          size="small"
                          aria-label="more"
                          onClick={() =>
                            navigate(PATHS.COMPONENT_TAGS_EDIT(String(row.id)))
                          }>
                          <EditOutlinedIcon />
                        </IconButton>
                      )}
                    </CustomTableCellRight>

                    <CustomTableCellRight>
                      {!isViewOnly && (
                        <IconButton
                          size="small"
                          aria-label="more"
                          data-testid={DataTestID.ManageComponentDeleteButton}
                          onClick={() => {
                            setSelectedComponentTag(row.id);
                            deleteConfirmDialog.onOpenModal(() =>
                              deleteTag(row),
                            );
                          }}>
                          <DeleteOutlinedIcon color="error" />
                        </IconButton>
                      )}
                    </CustomTableCellRight>
                  </CustomTableRow>
                )),
              )}
            </TableBody>
          </Table>
          {hasNextPage && (
            <InViewBoxContainer>
              <Box ref={inViewRef} />
            </InViewBoxContainer>
          )}
        </TableBodyContainer>
      ) : (
        <NoResultAlert />
      )}
      <SimpleDialog
        actionColor="error"
        open={deleteConfirmDialog.open}
        title={lang.componentTagDeleteModal.title}
        subTitle={lang.componentTagDeleteModal.subTitle}
        confirmText={lang.deleteText}
        cancelText={lang.cancel}
        onClose={deleteConfirmDialog.onCloseModal}
        onClickCancel={deleteConfirmDialog.onCloseModal}
        disabled={deletingStatus[selectedComponentTag]}
        onClickConfirm={deleteConfirmDialog.customFn}
      />
    </>
  );
};
